import React, {forwardRef, useEffect, useState} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import {useRouter} from 'next/router'

import IconClose from 'assets/icons/close.svg'
import IconEye from 'assets/icons/eye.svg'
import FieldsPayStubCompanyInfoNew from 'components/blocks/forms/FormPayStubNew/FieldsPayStubCompanyInfoNew'
import FieldsPayStubEarningsStatementNew from 'components/blocks/forms/FormPayStubNew/FieldsPayStubEarningsStatementNew'
import FieldsPayStubEmployeeInfoNew from 'components/blocks/forms/FormPayStubNew/FieldsPayStubEmployeeInfoNew'
import FieldsPayStubStartNew from 'components/blocks/forms/FormPayStubNew/FieldsPayStubStartNew'
import PayStubChooseTemplateNew from 'components/blocks/forms/FormPayStubNew/PayStubChooseTemplateNew'
import PaystubTemplatePreview from 'components/blocks/forms/FormPayStubNew/PaystubTemplatePreview'
import Button from 'components/elements/Button'
import {Col, Container, Row} from 'components/elements/grid'
import Progress from 'components/elements/Progress'
import Timeline from 'components/elements/Timeline'
import FieldsPayStubLastStep from 'src/components/blocks/forms/FormPayStubNew/FieldsPayStubLastStep'
import {posthogTrack} from 'src/utils'
import {useAuthStore, useDraftStore, usePayStubStore} from 'src/utils/contexts/RootStore.context'
import useAsyncFlow from 'src/utils/hooks/useAsyncFlow'
import {useMultistepForm} from 'src/utils/hooks/useMultistepForm'

import s from './FormPayStubNew.module.scss'
import {TemplatePagination} from './PayStubChooseTemplateNew/TemplatePagination'
import useChooseTemplate from '../hooks/useChooseTemplate'

const cx = cn.bind(s)

const payStubsTemplates: IPayStubTemplates[] = [
	{
		id: 1,
		isActive: true,
	},
	{
		id: 2,
		isActive: false,
	},
	{
		id: 3,
		isActive: false,
	},
	{
		id: 4,
		isActive: false,
		theme: 'gray',
	},
	{
		id: 5,
		isActive: false,
		theme: 'gray',
	},
	{
		id: 6,
		isActive: false,
		theme: 'gray',
	},
]

interface IFormPayStubNewProps {
	className?: string
}

const FormPayStubNew = forwardRef<HTMLDivElement, IFormPayStubNewProps>(({className}, ref) => {
	// const refsSteps = useRef<HTMLDivElement[]>([])
	const [isPreviewOverlayOpen, setIsPreviewOverlayOpen] = useState(false)
	const {templates, activeTemplate, handleTemplateTheme, handleActiveTemplate} =
		useChooseTemplate<IPayStubTemplates>(payStubsTemplates)

	const router = useRouter()
	const draftStore = useDraftStore()
	const authStore = useAuthStore()
	const payStubStore = usePayStubStore()

	const [saveDraftFlow, {isLoading, errorMessage}] = useAsyncFlow<typeof payStubStore.saveDraft>(
		payStubStore.saveDraft,
	)

	const paystubsCount = Number(payStubStore.payStubsNumber)

	const {steps, currentStepIndex, step, isFirstStep, isLastStep, totalSteps, back, next, goTo} =
		useMultistepForm([
			<div key="settings" data-step="1">
				<h2 className={cx('title')}>
					<span className={cx('title__desktop')}>Template Settings</span>
					<span className={cx('title__mobile')}>Choose a template</span>
				</h2>

				<PayStubChooseTemplateNew
					className="top-template"
					{...{templates, activeTemplate, handleTemplateTheme, handleActiveTemplate}}
				/>
			</div>,
			<div key="payment" data-step="2">
				<h2 className={cx('title')}>Payment INFORMATION</h2>

				<FieldsPayStubStartNew />
			</div>,
			<div key="company" data-step="3">
				<h2 className={cx('title')}>Company information</h2>

				<FieldsPayStubCompanyInfoNew />
			</div>,
			<div key="employee" data-step="4">
				<h2 className={cx('title')}>Employee information</h2>

				<FieldsPayStubEmployeeInfoNew />
			</div>,
			<div key="earning" data-step="5">
				<h2 className={cx('title')}>Earning statement</h2>

				<FieldsPayStubEarningsStatementNew />
			</div>,
			<div key="checkout">
				<h2 className={cx('title')}>Checkout</h2>

				<FieldsPayStubLastStep {...{activeTemplate, errorMessage}} />
			</div>,
		])

	const onCheckout = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		if (isLastStep) {
			// TODO: add analytics back
			if (!authStore.hasActiveSubscription) {
				posthogTrack(`[PAYSTUB unsubscribed] Submit button clicked`)
			}
			posthogTrack('[Paytub] Checkout button clicked')
			// pushGTMLayer({event: 'paystub_checkout_btn_clicked'})
			// klaviyoTrack('Checkout clicked', {
			// 	documentType: 'paystub',
			// 	hasActiveSubscription: authStore.hasActiveSubscription,
			// 	subscriptionType: authStore.user?.subscription?.subscriptionType,
			// })
			const draftId =
				draftStore.type === 'paystub' && draftStore.draftId ? draftStore.draftId : undefined
			const {id, theme} = activeTemplate as IPayStubTemplates
			const response = await saveDraftFlow(id, draftId, theme)()
			if (response?.data.ok) {
				draftStore.setDraft(response?.data.data.draftId, 'paystub', id, theme)

				if (!authStore.isLoggedIn) {
					return router.push('/auth/sign-up')
				}

				const canDownload = authStore.isLoggedIn && authStore.hasActiveSubscription
				router.push({
					pathname: canDownload ? '/download' : '/pricing',
				})
			}
		} else {
			next()
		}
	}

	useEffect(() => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth',
		})
	}, [ref, currentStepIndex])

	useEffect(() => {
		if (isPreviewOverlayOpen) {
			const documentWidth = document.documentElement.clientWidth
			const scrollBarWidth = Math.abs(window.innerWidth - documentWidth)
			document.body.style.paddingRight = `${scrollBarWidth}px`
			document.body.classList.add('no-scroll')
		} else {
			document.body.style.paddingRight = ''
			document.body.classList.remove('no-scroll')
		}
	}, [isPreviewOverlayOpen])

	const timelineItems = [
		'Template',
		'Payment information',
		'Company information',
		'Employee information',
		'Earning statement',
		'Checkout',
	]

	return (
		<form onSubmit={onCheckout} className={cx('wrapper', className)}>
			<button
				type="button"
				className={cx('overlay-toggle')}
				onClick={() => setIsPreviewOverlayOpen(!isPreviewOverlayOpen)}>
				{isPreviewOverlayOpen ? (
					<>
						<IconClose className={cx('overlay-toggle__icon')} />
						<span className={cx('overlay-toggle__text')}>Close preview</span>
					</>
				) : (
					<>
						<IconEye className={cx('overlay-toggle__icon')} />
						<span className={cx('overlay-toggle__text')}>Preview</span>
					</>
				)}
			</button>

			<Container className={cx('wrapper__container')}>
				<Row className={cx('wrapper__row')}>
					<Col md={2}>
						<Timeline className={cx('timeline')} active={currentStepIndex}>
							{timelineItems.map((timelineItem, index) => (
								<Timeline.Item
									key={timelineItem}
									{...(currentStepIndex > index
										? {
												onClick: () => goTo(index),
										  }
										: {})}>
									{timelineItem}
								</Timeline.Item>
							))}
						</Timeline>
					</Col>

					<Col md={5}>
						<div className={cx('progress')}>
							<Progress value={Math.floor(((currentStepIndex + 1) / totalSteps) * 100)} />
						</div>

						{step}

						<div className={cx('actions')}>
							<Button
								className={cx('actions__button')}
								variant="secondary"
								type="button"
								onClick={back}
								disabled={isFirstStep}>
								Back
							</Button>

							<div className={cx('pagination')}>
								{steps.map((_, idx) => (
									<div
										key={idx}
										className={cx('pagination__item', currentStepIndex === idx && 'active')}
									/>
								))}
							</div>

							<Button
								className={cx('actions__button', 'actions__next')}
								type="submit"
								size="big"
								disabled={
									isLastStep &&
									(!!payStubStore.errorMessage || !payStubStore?.terms || !payStubStore?.privacy)
								}
								isLoading={isLoading}>
								{isLastStep ? 'Continue' : 'Next'}
							</Button>
						</div>
					</Col>

					<Col md={5}>
						<div className={cx('preview', 'preview--overlay', isPreviewOverlayOpen && 'open')}>
							<div className={cx('preview__template')}>
								<PaystubTemplatePreview activeTemplate={activeTemplate} />
							</div>

							{paystubsCount > 1 ? (
								<div className={cx('preview__pagination')}>
									<TemplatePagination
										payStubs={paystubsCount}
										setActiveDataTemplate={payStubStore.setActiveDataTemplate}
										activeDataTemplate={payStubStore.activeDataTemplate}
									/>
								</div>
							) : null}
						</div>
					</Col>
				</Row>
			</Container>
		</form>
	)
})

FormPayStubNew.displayName = 'FormPayStubNew'

export default observer(FormPayStubNew)
